import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

// 인증이 필요하지 않은 페이지 경로 목록
const PUBLIC_PATHS = ["/", "/select", "/complete", "/check-status", "/status"];

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
  const currentPath = location.pathname;
  const [showTokenExpiredModal, setShowTokenExpiredModal] = useState(false);

  // 현재 경로가 퍼블릭 경로인지 확인
  const isPublicPath = PUBLIC_PATHS.includes(currentPath);

  // 토큰 유효성 검사
  useEffect(() => {
    const verifyToken = async () => {
      if (token && !isPublicPath) {
        try {
          // 토큰 검증 API 호출
          const response = await fetch(
            `${API_ENDPOINTS.BASE_URL}/auth/verify-token`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                token: token,
              }),
            }
          );

          // 토큰이 유효하지 않은 경우
          if (response.status !== 201) {
            setShowTokenExpiredModal(true);
          }
        } catch (error) {
          setShowTokenExpiredModal(true);
        }
      }
    };

    verifyToken();
  }, [token, isPublicPath, currentPath]);

  // 모달 닫기 및 메인 페이지 이동
  const handleModalClose = () => {
    // 토큰 및 관련 쿠키 삭제
    Cookies.remove(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);

    // 모달 닫기
    setShowTokenExpiredModal(false);

    // 메인 페이지로 이동
    navigate("/select");
  };

  // 모달 애니메이션 설정
  const modalVariants = {
    initial: {
      opacity: 0,
      scale: 0.8,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  // 토큰이 없고 퍼블릭 경로가 아니면 Onboarding으로 리다이렉트
  if (!token && !isPublicPath) {
    return <Navigate to="/" replace />;
  }

  // 모든 조건을 통과하면 자식 컴포넌트 렌더링
  return (
    <>
      {children}

      {/* 토큰 만료 모달 */}
      <AnimatePresence>
        {showTokenExpiredModal && (
          <ModalOverlay>
            <ModalContainer
              variants={modalVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ModalContent>
                <ModalTitle>인증 세션 만료</ModalTitle>
                <ModalText>
                  인증 세션이 만료되었습니다.
                  <br />
                  1시간이 지나 처음부터 다시 이용해주세요.
                </ModalText>
                <ModalButton onClick={handleModalClose}>확인</ModalButton>
              </ModalContent>
            </ModalContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  touch-action: none;
`;

const ModalContainer = styled(motion.div)`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
  width: 100%;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: center;
`;

export default ProtectedRoute;
